<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { isIgnoredGalleryAttachment, isMediaAttachment } from './galleryUtils';
import MediaGalleryPreview from './ContactGalleryMediaPreview.vue';
import DocumentsGalleryPreview from './ContactGalleryDocumentsPreview.vue';

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
  contactId: {
    type: Number,
    required: true,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const loading = ref(false);
const error = ref(null);
const activeTab = ref('media');
const { t } = useI18n();
const store = useStore();
const contactAttachmentsGetter = useMapGetter('contacts/getContactAttachments');

const galleryAttachments = computed(() => {
  const getContactAttachments = contactAttachmentsGetter.value;

  if (!props.contactId || typeof getContactAttachments !== 'function') {
    return [];
  }

  return getContactAttachments(props.contactId);
});

const visibleAttachments = computed(() =>
  galleryAttachments.value.filter(
    attachment => !isIgnoredGalleryAttachment(attachment)
  )
);

const mediaCount = computed(
  () => visibleAttachments.value.filter(isMediaAttachment).length
);

const docsCount = computed(
  () =>
    visibleAttachments.value.filter(
      attachment => !isMediaAttachment(attachment)
    ).length
);

const tabs = computed(() => [
  {
    key: 'media',
    name: t('GALLERY_VIEW.MEDIA'),
    count: mediaCount.value,
  },
  {
    key: 'docs',
    name: t('GALLERY_VIEW.DOCS'),
    count: docsCount.value,
  },
]);

const activeTabIndex = computed(() =>
  Math.max(
    0,
    tabs.value.findIndex(tab => tab.key === activeTab.value)
  )
);

const fetchAttachments = async () => {
  error.value = null;
  loading.value = true;

  try {
    await store.dispatch('contacts/fetchContactAttachments', {
      accountId: props.accountId,
      contactId: props.contactId,
      force: true,
    });
  } catch (err) {
    error.value = err;
  } finally {
    loading.value = false;
  }
};

const onTabChange = selectedTabIndex => {
  const selectedTab = tabs.value[selectedTabIndex];
  if (!selectedTab || selectedTab.key === activeTab.value) return;

  activeTab.value = selectedTab.key;
};

watch(
  () => props.contactId,
  (newContactId, oldContactId) => {
    if (!newContactId || newContactId === oldContactId) return;

    activeTab.value = 'media';
    fetchAttachments();
  },
  { immediate: true }
);

watch(
  () => props.isOpen,
  (newIsOpen, oldIsOpen) => {
    if (newIsOpen === oldIsOpen || !props.contactId) return;

    fetchAttachments();
  }
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <woot-tabs
      class="w-full px-2 -mt-1 py-0 [&_ul]:p-0 h-10"
      :index="activeTabIndex"
      @change="onTabChange"
    >
      <woot-tabs-item
        v-for="(tab, index) in tabs"
        :key="tab.key"
        :index="index"
        :name="tab.name"
        :count="tab.count"
        class="text-sm [&_a]:font-medium"
        is-compact
      />
    </woot-tabs>

    <MediaGalleryPreview
      v-if="activeTab === 'media'"
      :attachments="visibleAttachments"
      :loading="loading"
      :error="error"
    />
    <DocumentsGalleryPreview
      v-else
      :attachments="visibleAttachments"
      :loading="loading"
      :error="error"
    />
  </div>
</template>
